import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import NamedRedirect from '../../components/NamedRedirect/NamedRedirect';
// FairwayBid: Seiten fuer die jeweils andere Seite des Marktplatzes wegnehmen
import { getAudience, mayViewPage, ANONYMOUS } from '../../fairwaybid/audience';
// FairwayBid: Die Haendlerseite bekommt einen eigenen, gestalteten Baustein.
import SectionDealer from '../../fairwaybid/SectionDealer/SectionDealer';
import SectionClub from '../../fairwaybid/SectionClub/SectionClub';
import { DEALER_PAGE_ID, DEALER_TYPE } from '../../fairwaybid/dealerPage';
import { CLUB_PAGE_ID, CLUB_TYPE } from '../../fairwaybid/clubPage';
import SectionHowItWorks from '../../fairwaybid/SectionHowItWorks/SectionHowItWorks';
import { HOWTO_PAGE_ID, HOWTO_TYPE } from '../../fairwaybid/howItWorks';
import SectionPlanCards from '../../fairwaybid/SectionPlanCards/SectionPlanCards';
import SectionNotes from '../../fairwaybid/SectionNotes/SectionNotes';
import SectionRules from '../../fairwaybid/SectionRules/SectionRules';
import SectionImprint from '../../fairwaybid/SectionImprint/SectionImprint';
import SectionAbout from '../../fairwaybid/SectionAbout/SectionAbout';
import { IMPRINT_PAGE_ID, IMPRINT_TYPE } from '../../fairwaybid/imprint';
import { ABOUT_PAGE_ID, ABOUT_TYPE } from '../../fairwaybid/aboutPage';

// Die uebrigen Abschnitte von "So funktioniert es" bekommen ebenfalls einen
// eigenen Baustein - Inhalt bleibt in der Console, nur die Darstellung
// kommt von uns.
const PLANS_TYPE = 'fwb-pakete';
const NOTES_TYPE = 'fwb-regeln';
// "Was es kostet" war Fliesstext ueber drei Regeln und drei Fristen. Als
// Zeitleiste sieht man den Ablauf, statt ihn lesen zu muessen.
const RULES_TYPE = 'fwb-fristen';

// Die beiden Verkaufsseiten bekommen einen eigenen, gestalteten Baustein.
// Alle anderen Seiten laufen unveraendert durch den Standard-PageBuilder.
const VERKAUFSSEITEN = {
  [DEALER_PAGE_ID]: { typ: DEALER_TYPE, component: SectionDealer },
  [CLUB_PAGE_ID]: { typ: CLUB_TYPE, component: SectionClub },
  // Das Impressum ist keine Verkaufsseite, aber derselbe Fall: Ueberschrift
  // aus der Console, Inhalt aus dem Code. Die Angaben muessen an genau einer
  // Stelle stimmen - siehe src/fairwaybid/imprint.js
  [IMPRINT_PAGE_ID]: { typ: IMPRINT_TYPE, component: SectionImprint },
  // "Ueber uns" bestand aus neun gleich aussehenden Textabschnitten. Der
  // Baustein uebernimmt die ganze Seite, deshalb "nurErster": alles Weitere
  // aus der Console faellt weg. Inhalt: src/fairwaybid/aboutPage.js
  [ABOUT_PAGE_ID]: { typ: ABOUT_TYPE, component: SectionAbout, nurErster: true },
  // Auf "So funktioniert es" ersetzen die beiden Vorgaenge auch die zwei
  // Schrittlisten "Fuer Golfanlagen" und "Fuer Haendler" - sie sagen
  // dasselbe, nur ohne Geschichte. Die uebrigen Abschnitte der Seite
  // (Kosten, Regeln, Preise) bleiben unberuehrt.
  [HOWTO_PAGE_ID]: {
    typ: HOWTO_TYPE,
    component: SectionHowItWorks,
    ohneTyp: 'features',
    weitere: {
      preise: { typ: PLANS_TYPE, component: SectionPlanCards },
      kosten: { typ: RULES_TYPE, component: SectionRules },
      'mitgliedschaft-details': { typ: NOTES_TYPE, component: SectionNotes },
    },
  },
};
const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const CMSPageComponent = props => {
  const { params, pageAssetsData, inProgress, error, currentUser } = props;
  const pageId = params.pageId || props.pageId;

  // Der Server rendert ohne angemeldeten Nutzer; erst nach dem Mounten steht
  // fest, wer davorsitzt.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!inProgress && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const audience = hasMounted ? getAudience(currentUser) : ANONYMOUS;
  if (!mayViewPage(pageId, audience)) {
    return <NamedRedirect name="LandingPage" />;
  }

  // FairwayBid: Auf der Haendlerseite bleibt die Ueberschrift aus der Console,
  // den Rest uebernimmt ein eigener Baustein - Argumente, Ablauf und Tarife.
  // Alle anderen Seiten laufen unveraendert durch den Standard-PageBuilder.
  const assetData = pageAssetsData?.[pageId]?.data;
  const verkaufsseite = VERKAUFSSEITEN[pageId];
  const seitenDaten =
    verkaufsseite && assetData
      ? {
          ...assetData,
          sections: (assetData.sections || [])
            .filter(
              (section, i) =>
                i === 0 ||
                (!verkaufsseite.nurErster && section.sectionType !== verkaufsseite.ohneTyp)
            )
            .map((section, i) => {
              if (i === 0) {
                return { ...section, sectionType: verkaufsseite.typ };
              }
              const weiter = verkaufsseite.weitere?.[section.sectionId];
              return weiter ? { ...section, sectionType: weiter.typ } : section;
            }),
        }
      : assetData;
  const optionsMaybe = verkaufsseite
    ? {
        options: {
          sectionComponents: {
            [verkaufsseite.typ]: { component: verkaufsseite.component },
            ...Object.values(verkaufsseite.weitere || {}).reduce(
              (acc, w) => ({ ...acc, [w.typ]: { component: w.component } }),
              {}
            ),
          },
        },
      }
    : {};

  return (
    <PageBuilder
      pageAssetsData={seitenDaten}
      inProgress={inProgress}
      schemaType="Article"
      featuredListings={getFeaturedListingsProps(pageId, props)}
      {...optionsMaybe}
    />
  );
};

CMSPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { currentUser } = state.user || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return {
    pageAssetsData,
    featuredListingData,
    getListingEntitiesById,
    inProgress,
    error,
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CMSPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CMSPageComponent);

export default CMSPage;
